'use client';

import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PulseIconButton, PulseButton } from 'src/components/PulseUI';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { useLogging } from 'src/context/logging';
import { useSite } from 'src/context/providers/SiteProvider';
import { AddEditActivity } from '../AddEditActivity';

interface NewActivityLogProps {
  isSmall?: boolean;
  testIdPrefix?: string;
}

const NewActivityLog: React.FC<NewActivityLogProps> = ({
  isSmall = false,
  testIdPrefix = '',
}) => {
  const log = useLogging();
  const { state, dispatch } = useSite();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const portalRef = React.useRef<HTMLDivElement>(null);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
    log.event('Activity Log Button Clicked');
  }, [log]);

  const handleCancel = useCallback(() => {
    if (state?.activity?.hasData) {
      if (
        !confirm('You have unsaved changes. Are you sure you want to close?')
      ) {
        return;
      }
    }
    setIsOpen(false);
    dispatch({ type: 'SET_HAS_DATA', payload: false });
  }, [state?.activity?.hasData, dispatch]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Ignore clicks on select menus
      const isSelectMenu =
        target.closest('[role="listbox"]') ||
        target.closest('[role="presentation"]');

      if (isSelectMenu) {
        return;
      }

      // Check if click is inside the button or portal
      if (dropdownRef.current?.contains(target)) {
        return;
      }

      if (portalRef.current?.contains(target)) {
        return;
      }

      handleCancel();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleCancel]);

  return (
    <div
      className="relative"
      ref={dropdownRef}
      data-testid="activity-log-button"
    >
      {isSmall ? (
        <PulseIconButton
          icon={faBolt}
          variant="solid"
          color="primary"
          size="sm"
          onClick={toggleMenu}
          aria-label="Log Activity"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          data-testid={
            testIdPrefix
              ? `${testIdPrefix}-log-activity-button`
              : 'log-activity-button'
          }
          tooltip="Log Activity"
          tooltipPosition="bottom"
        />
      ) : (
        <PulseButton
          startDecorator={faBolt}
          variant={'outline'}
          color="neutral"
          size="sm"
          onClick={toggleMenu}
          className="whitespace-nowrap bg-white"
          aria-label="Log Activity"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          data-testid={
            testIdPrefix
              ? `${testIdPrefix}-log-activity-button`
              : 'log-activity-button'
          }
        >
          Log Activity
        </PulseButton>
      )}
      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={portalRef}
            className="fixed z-[10000] w-96"
            // Exception: Dynamic portal positioning requires inline styles for getBoundingClientRect values
            style={{
              left:
                (dropdownRef.current?.getBoundingClientRect().right ?? 0) - 384,
              top: dropdownRef.current?.getBoundingClientRect().bottom ?? 0,
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="activity-log-dropdown"
          >
            <div className="bg-transparent rounded-lg shadow-lg mt-1">
              <AddEditActivity
                onClose={handleClose}
                onCancel={handleCancel}
                checkHasData
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NewActivityLog;
